<script setup lang="ts">
import { ref, watch, provide } from 'vue'
import { GeoImagesViewModel } from '../models/GeoImages.viewmodel'
import { GeoImagesViewModelKey } from '../keys/GeoImagesViewModel.key'

import Button from '../../../../components/Button/Button.vue'
import ExclusionAlert from '../../../../components/ExclusionAlert/ExclusionAlert.vue'
import InputSearch from '../../../../components/InputSearch/InputSearch.vue'
import DropdownCheckbox from '../../../../components/DropdownCheckbox/DropdownCheckbox.vue'
import CrudTable from '../../../../components/CrudTable/CrudTable.vue'
import KebabMenu from '../../../../components/KebabMenu/KebabMenu.vue'
import Pagination from '../../../../components/Pagination/Pagination.vue'
import CrudForm from '../../../../components/CrudForm/CrudForm.vue'
import GeoImageForm from '../components/GeoImageForm.vue'
import MapBandsForm from '../components/MapBandsForm.vue'
import GeoImageValidationStep from '../components/GeoImageValidationStep.vue'
import StorageUsageBanner from '../components/StorageUsageBanner.vue'
import { Portal } from '@linusborg/vue-simple-portal'

const vm = new GeoImagesViewModel()
provide(GeoImagesViewModelKey, vm)

vm.fetchAll()
vm.fetchStorageUsage()

const ref__pagination = ref()

watch(vm.textFilter, () => {
	ref__pagination.value?.resetToFirstPage()
})

watch(
	vm.tableHeaders,
	(value) => {
		vm.headersSelecionados.value = value.filter((f) => f.isActive).map((m) => m.key)
	},
	{ deep: true },
)

watch(vm.intention, (value) => {
	const model = vm.modelList.value.find((m) => m.id === value.properties?.id)
	if (!model) return

	switch (value.intent) {
		case 'editar':
			vm.showEditModelForm(model)
			break
	}
})
</script>

<template>
	<div class="container__crud">
		<div class="container__crudactions">
			<div class="container__crudactions__cruddownload">
				<div class="container__crudactions__cruddownload__crud">
					<Button
						label="Excluir Selecionados"
						color="trv-error"
						outlined
						icon="far fa-trash-alt"
						semantic-class="tiny"
						:disabled="vm.isDeleteButtonDisabled.value"
						width-auto
						@click="vm.toggleExclusionAlert()"
					/>
					<Button
						label="Cadastrar"
						color="trv-default"
						semantic-class="tiny"
						width-auto
						:disabled="vm.isStorageFull.value"
						@click="vm.showNewModelForm()"
					/>
					<ExclusionAlert
						v-if="vm.showExclusionAlert.value"
						:items-to-delete="vm.modelList.value.filter((item) => item.isCheckedToDelete)"
						:message="
							vm.modelList.value.filter((item) => item.isCheckedToDelete).length > 1
								? 'Tem certeza que deseja excluir as imagens cadastradas?'
								: 'Tem certeza que deseja excluir a imagem cadastrada?'
						"
						@cancel="vm.toggleExclusionAlert()"
						@confirm="vm.deleteGeoImages()"
					>
						<template #item="{ item }">
							<li class="exclusion-alert-item">{{ item.code }} - {{ item.name }}</li>
						</template>
					</ExclusionAlert>
				</div>
			</div>
			<div class="container__crudactions__filtercollums">
				<div class="container__crudactions__filtercollums__filter">
					<InputSearch
						v-model="vm.textFilter.value"
						style="margin-right: 0.5rem"
						placeholder="Pesquisar"
					/>
				</div>
				<div
					class="container__crudactions__filtercollums__collumns"
					style="display: flex"
				>
					<DropdownCheckbox v-model="vm.tableHeaders.value" />
					<Button
						label="Exportar"
						semantic-class="mini"
						color="trv-primary"
						outlined
						width-auto
						style="border-radius: 5px !important; min-width: 65px"
						@click="vm.downloadCSV()"
					/>
				</div>
			</div>
		</div>
		<CrudTable
			:headers="vm.headersAtivos.value"
			:data="vm.modelsToBeShowedAndTotal.value.items"
			:table-configuration="vm.tableConfiguration.value"
			:show-edit-model-form="vm.showEditModelForm.bind(vm)"
			@sortConfig="
				vm.sortBy.value = $event.sortBy
				vm.sortType.value = $event.sortType
			"
		>
			<template #outer-action-button="dataObject">
				<KebabMenu
					v-model="vm.intention.value"
					:options="vm.kebabOptions.value"
					:properties="dataObject"
				/>
			</template>
		</CrudTable>
		<div class="container__crud__pagination">
			<Pagination
				ref="ref__pagination"
				:quantity-per-page="10"
				:quantity-per-page-options="[10, 20, 30]"
				:total-records="vm.modelsToBeShowedAndTotal.value.total"
				@setStartEnd="vm.setStartEnd($event)"
				@setQuantityPerPage="vm.setQuantityPerPage($event)"
			/>
		</div>
		<CrudForm
			:is-visible="vm.showForm.value && vm.formStep.value !== 2"
			:change-is-visible="vm.changeFormAndCloseAllActions.bind(vm)"
			:backdrop="true"
		>
			<template #form>
				<GeoImageForm v-if="vm.formStep.value === 0" />
				<MapBandsForm v-else-if="vm.formStep.value === 1" />
			</template>
		</CrudForm>
		<GeoImageValidationStep v-if="vm.showForm.value && vm.formStep.value === 2" />
		<portal
			v-if="vm.storageUsage.value"
			selector="#banner"
		>
			<div class="mt-8">
				<storage-usage-banner
					:usage="vm.storageUsage.value"
					@requestStorage="vm.requestStorageIncrease()"
				/>
			</div>
		</portal>
	</div>
</template>

<style scoped src="../../../../css/crud.css"></style>
